import React, { useMemo } from 'react';
import { formatDate } from '../../../config/appConfig';
import { formatHm } from '../../../components/timeline-grid';
import {
  ReportBody,
  ReportField,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportPack,
  ReportPage,
  ReportTitleRow,
} from '../../../components/ui/Report';

export type VesselOpType = { id: number; name: string; color: string };

export type VesselOpSegment = {
  operationTypeId: number;
  startMinute: number;
  endMinute: number;
  operationType?: VesselOpType;
};

export type VesselOpDay = {
  workDate: string;
  vesselStatus: 'AT_SEA' | 'IN_PORT';
  notes: string | null;
  segments: VesselOpSegment[];
};

export type VesselOperationsReportData = {
  vesselId: number;
  vesselName: string;
  vesselAbrv?: string | null;
  managementCompany?: {
    id: number;
    name: string;
    code?: string | null;
    logoStorageKey?: string | null;
    logoOriginalFilename?: string | null;
  } | null;
  month: string;
  types: VesselOpType[];
  days: VesselOpDay[];
};

function monthYearLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function statusLabel(s: VesselOpDay['vesselStatus']): string {
  return s === 'IN_PORT' ? 'In port' : 'At sea';
}

function typeColor(seg: VesselOpSegment, types: VesselOpType[]): string {
  return seg.operationType?.color || types.find((t) => t.id === seg.operationTypeId)?.color || '#a1a1aa';
}

function typeName(seg: VesselOpSegment, types: VesselOpType[]): string {
  return seg.operationType?.name || types.find((t) => t.id === seg.operationTypeId)?.name || `Type #${seg.operationTypeId}`;
}

function formatSegments(segments: VesselOpSegment[], types: VesselOpType[]): React.ReactNode {
  if (!segments.length) return <span className="vo-ops-muted">—</span>;
  const sorted = [...segments].sort((a, b) => a.startMinute - b.startMinute);
  return (
    <span className="vo-ops-segs">
      {sorted.map((s, i) => (
        <span key={`${s.operationTypeId}-${s.startMinute}-${i}`} className="vo-ops-seg">
          <span className="vo-ops-swatch" style={{ backgroundColor: typeColor(s, types) }} />
          <span className="vo-ops-seg-text">
            {formatHm(s.startMinute)}–{formatHm(s.endMinute)} {typeName(s, types)}
          </span>
        </span>
      ))}
    </span>
  );
}

type VesselOperationsReportProps = {
  data: VesselOperationsReportData;
  companyLogoSrc?: string | null;
};

export const VesselOperationsReport: React.FC<VesselOperationsReportProps> = ({
  data,
  companyLogoSrc = null,
}) => {
  const monthLabel = monthYearLabel(data.month);
  const vesselTitle = `${data.vesselName}${data.vesselAbrv ? ` (${data.vesselAbrv})` : ''}`;
  const segmentCount = useMemo(
    () => data.days.reduce((n, d) => n + d.segments.length, 0),
    [data.days],
  );
  const typesUsed = useMemo(() => {
    const ids = new Set<number>();
    for (const d of data.days) {
      for (const s of d.segments) ids.add(s.operationTypeId);
    }
    const used = data.types.filter((t) => ids.has(t.id));
    return used.length ? used : data.types;
  }, [data.days, data.types]);

  return (
    <ReportPack>
      <ReportPage size="landscape">
        <ReportHeader>
          <ReportTitleRow
            title="Vessel operations schedule"
            formId="Month plan"
            logoSrc={companyLogoSrc}
            logoAlt={data.managementCompany?.name || 'Management company logo'}
          />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselTitle} />
              <ReportField label="Month" value={monthLabel} />
              <ReportField
                label="Summary"
                value={`${data.days.length} days · ${segmentCount} operation segment${segmentCount === 1 ? '' : 's'}`}
              />
            </ReportMetaRow>
          </ReportMeta>
          {typesUsed.length > 0 ? (
            <div className="vo-ops-legend" aria-label="Operation types">
              {typesUsed.map((t) => (
                <span key={t.id} className="vo-ops-legend-item">
                  <span className="vo-ops-swatch" style={{ backgroundColor: t.color }} />
                  {t.name}
                </span>
              ))}
            </div>
          ) : null}
        </ReportHeader>

        <ReportBody>
          <table className="vo-ops-grid">
            <thead>
              <tr>
                <th className="vo-ops-date">Date</th>
                <th className="vo-ops-status">Status</th>
                <th className="vo-ops-ops">Operations</th>
                <th className="vo-ops-notes">Notes</th>
              </tr>
            </thead>
            <tbody>
              {data.days.map((day) => {
                const isWeekend = [0, 6].includes(
                  new Date(`${day.workDate}T12:00:00Z`).getUTCDay(),
                );
                return (
                  <tr key={day.workDate} className={isWeekend ? 'vo-ops-weekend' : undefined}>
                    <td className="vo-ops-date">{formatDate(day.workDate)}</td>
                    <td className="vo-ops-status">{statusLabel(day.vesselStatus)}</td>
                    <td className="vo-ops-ops">{formatSegments(day.segments, data.types)}</td>
                    <td className="vo-ops-notes">{day.notes?.trim() || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ReportBody>

        <ReportFooter>
          <ReportNote>
            Operation type colours match the vessel operations schedule and WRH month-sheet overlays.
          </ReportNote>
        </ReportFooter>
      </ReportPage>
    </ReportPack>
  );
};

export default VesselOperationsReport;
